import Input from "@/v2-ui/input"
import Select from "@/v2-ui/select"
import cn from "@/v2-ui/utils/utils.cn"
import Button, { TooltipButton } from "@/v2-backoffice-client/components/button"
import type { FilterCondition, FilterOperator } from "../table.types"
import {
  isMultiValueOperator,
  isUnaryOperator,
  operatorLabel,
  operatorsForColumn,
  type AdvancedFilterColumn,
} from "./tableAdvancedFilter.utils"

export type TableAdvancedFilterConditionProps = {
  condition: FilterCondition
  columns: AdvancedFilterColumn[]
  onChange(next: FilterCondition): void
  onRemove(): void
}

function TableAdvancedFilterCondition(
  props: TableAdvancedFilterConditionProps,
) {
  const { condition, columns, onChange, onRemove } = props

  const column = columns.find((c) => c.id === condition.column)
  const filterType = column?.filter.type ?? "text"
  const operators = column
    ? operatorsForColumn(column)
    : (["EQ"] as FilterOperator[])

  function onColumnChange(columnId: string) {
    const nextColumn = columns.find((c) => c.id === columnId)
    const nextOperators = nextColumn
      ? operatorsForColumn(nextColumn)
      : (["EQ"] as FilterOperator[])
    // Keep the current operator if still valid, else reset to the default.
    const operator = nextOperators.includes(condition.operator)
      ? condition.operator
      : nextOperators[0]
    onChange({
      ...condition,
      column: columnId,
      operator,
      value: null,
      values: null,
    })
  }

  function onOperatorChange(operator: FilterOperator) {
    onChange({ ...condition, operator, value: null, values: null })
  }

  return (
    <div className="ui-table-adv-filter-condition">
      <TooltipButton
        variant={condition.negate ? "secondary" : "ghost"}
        size="xs"
        className="ui-table-adv-filter-not"
        content="Negate this condition"
        onClick={() => onChange({ ...condition, negate: !condition.negate })}
      >
        NOT
      </TooltipButton>

      <Select value={condition.column} onValueChange={onColumnChange}>
        <Select.Trigger size="xs" className="ui-table-adv-filter-column">
          <Select.Value placeholder="Column" />
        </Select.Trigger>
        <Select.Content>
          {columns.map((c) => (
            <Select.Item key={c.id} value={c.id}>
              {c.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>

      <Select
        value={condition.operator}
        onValueChange={(v) => onOperatorChange(v as FilterOperator)}
      >
        <Select.Trigger size="xs" className="ui-table-adv-filter-operator">
          <Select.Value placeholder="Operator" />
        </Select.Trigger>
        <Select.Content>
          {operators.map((op) => (
            <Select.Item key={op} value={op}>
              {operatorLabel(op, filterType)}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>

      <ConditionValueInput
        condition={condition}
        column={column}
        onChange={onChange}
      />

      <TooltipButton
        variant="ghost"
        size="xs"
        isSquare
        iconSrc="cross-small"
        className="ui-table-adv-filter-remove"
        content="Remove condition"
        onClick={onRemove}
      />
    </div>
  )
}

type ConditionValueInputProps = {
  condition: FilterCondition
  column: AdvancedFilterColumn | undefined
  onChange(next: FilterCondition): void
}

function ConditionValueInput(props: ConditionValueInputProps) {
  const { condition, column, onChange } = props
  const filterType = column?.filter.type ?? "text"

  if (isUnaryOperator(condition.operator)) {
    return <div className="ui-table-adv-filter-value-empty" />
  }

  if (isMultiValueOperator(condition.operator)) {
    const value = (condition.values ?? []).join(", ")
    return (
      <Input
        type="text"
        size="xs"
        placeholder="Comma-separated values"
        value={value}
        onChange={(e) =>
          onChange({
            ...condition,
            values: e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          })
        }
        className={cn("ui-table-adv-filter-value")}
      />
    )
  }

  if (filterType === "select") {
    return (
      <Select
        value={condition.value ?? ""}
        onValueChange={(v) => onChange({ ...condition, value: v })}
      >
        <Select.Trigger size="xs" className="ui-table-adv-filter-value">
          <Select.Value placeholder={column?.filter.placeholder ?? "Value"} />
        </Select.Trigger>
        <Select.Content>
          {column?.filter.options?.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select>
    )
  }

  if (filterType === "boolean") {
    return (
      <Select
        value={condition.value ?? ""}
        onValueChange={(v) => onChange({ ...condition, value: v })}
      >
        <Select.Trigger size="xs" className="ui-table-adv-filter-value">
          <Select.Value placeholder="Value" />
        </Select.Trigger>
        <Select.Content>
          <Select.Item value="1">Yes</Select.Item>
          <Select.Item value="0">No</Select.Item>
        </Select.Content>
      </Select>
    )
  }

  const isDate =
    filterType === "date" ||
    filterType === "date-range" ||
    filterType === "date-filter"
  const isNumber = filterType === "number" || filterType === "number-range"

  return (
    <Input
      type={isDate ? "date" : isNumber ? "number" : "text"}
      size="xs"
      placeholder={column?.filter.placeholder ?? "Value"}
      value={condition.value ?? ""}
      onChange={(e) => onChange({ ...condition, value: e.target.value })}
      className="ui-table-adv-filter-value"
    />
  )
}

export default TableAdvancedFilterCondition
